import "../../../../.cache/static-styles-react/build-javascript/compiled.js";
/** @jsxRuntime automatic @jsxImportSource @terraindigital/static-styles-react */
import { cssRef as _cssRef, mergeComponentProps as _mergeComponentProps, px as _px, withVars as _withVars } from "@terraindigital/static-styles-react";
const _staticStyles = _cssRef("props-browser__propsbrowseroverlaycss-2g6amt", {
  padding: "24px 16px"
});
const _staticStyles2 = _cssRef("props-browser__propsbrowsercontentcss-2k8i3u", {
  width: "min(90vw, 1200px)",
  maxWidth: "1200px",
  maxHeight: "min(85vh, calc(100vh - 48px))",
  padding: 25,
  backgroundColor: "white",
  borderRadius: "8px",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden"
});
const _staticStyles3 = _cssRef("props-browser__modaldescription-1ess7s", {
  position: "absolute",
  left: "-10000px",
  width: "1px",
  height: "1px",
  overflow: "hidden"
});
const _staticStyles4 = _cssRef("props-browser__button-0ok2ij", {
  backgroundColor: "var(--static-styles-3vel4qkc_backgroundColor)",
  color: "#fff",
  padding: "6px 12px",
  borderRadius: "4px",
  fontSize: "14px",
  transition: "0.2s",
  border: "none",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "var(--static-styles-21gxuxye_backgroundColor)"
  }
});
const _staticStyles5 = _cssRef("props-browser__modalclosebutton-1c1l4q", {
  position: "relative",
  top: "auto",
  right: "auto",
  backgroundColor: "#ef4444",
  color: "#fff",
  padding: "6px 10px",
  borderRadius: "4px",
  fontSize: "18px",
  fontWeight: "bold",
  transition: "0.2s",
  border: "none",
  cursor: "pointer",
  height: "32px",
  width: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    backgroundColor: "#dc2626"
  }
});
const _staticStyles6 = _cssRef("props-browser__button-0j08qc", {
  backgroundColor: "#6b7280",
  color: "#fff",
  padding: "6px 10px",
  borderRadius: "4px",
  fontSize: "12px",
  border: "none",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "#4b5563"
  }
});
const _staticStyles7 = _cssRef("props-browser__button-0u1bxe", {
  backgroundColor: "#f3f4f6",
  color: "#111",
  padding: "6px 10px",
  borderRadius: "4px",
  fontSize: "12px",
  border: "1px solid #ddd",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "#e5e7eb"
  }
});
import React from 'react';
import safeJsonValue from 'safe-json-value';
import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';
import Button from '../button';
import { Modal, ModalCloseButton, ModalContent, ModalDescription, ModalTitle, ModalTrigger } from '../modal';
import Typography from '../typography';
import DebugPill from './debug-pill';
type PropsBrowserProps = Record<string, unknown>;
const DEFAULT_COLLAPSED_DEPTH = 2;
const propsBrowserOverlayCss = _staticStyles;
const propsBrowserContentCss = _staticStyles2;

/** Prune `value` to branches whose keys or primitive values match `query` (case-insensitive). Key match keeps the full subtree. */
function filterJsonByQuery(value: unknown, queryRaw: string): unknown | undefined {
  const query = queryRaw.trim().toLowerCase();
  if (!query) {
    return value;
  }
  if (value === null || typeof value !== 'object') {
    return String(value).toLowerCase().includes(query) ? value : undefined;
  }
  if (Array.isArray(value)) {
    const next = value.map(item => filterJsonByQuery(item, query)).filter((item): item is NonNullable<typeof item> => item !== undefined);
    return next.length > 0 ? next : undefined;
  }
  const obj = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  Object.entries(obj).forEach(([key, v]) => {
    const keyMatch = key.toLowerCase().includes(query);
    if (keyMatch) {
      out[key] = v;
    } else {
      const filteredChild = filterJsonByQuery(v, query);
      if (filteredChild !== undefined) {
        out[key] = filteredChild;
      }
    }
  });
  return Object.keys(out).length > 0 ? out : undefined;
}
type CollapsedMode = 'depth' | 'expandAll' | 'collapseAll';
function collapsedPropForMode(mode: CollapsedMode): boolean | number {
  if (mode === 'expandAll') {
    return false;
  }
  if (mode === 'collapseAll') {
    return true;
  }
  return DEFAULT_COLLAPSED_DEPTH;
}
const PropsBrowser: React.FC<PropsBrowserProps> = props => {
  const [copied, setCopied] = React.useState(false);
  const [filterQuery, setFilterQuery] = React.useState('');
  const [collapsedMode, setCollapsedMode] = React.useState<CollapsedMode>('depth');
  const [treeKey, setTreeKey] = React.useState(0);
  const safeValue = React.useMemo(() => safeJsonValue(props).value, [props]);
  const filteredValue = React.useMemo(() => filterJsonByQuery(safeValue, filterQuery), [safeValue, filterQuery]);
  const collapsedProp = collapsedPropForMode(collapsedMode);
  const handleCopy = async (): Promise<void> => {
    const isFiltered = filterQuery.trim() !== '';
    const toCopy = isFiltered ? filteredValue ?? null : safeValue;
    const jsonString = JSON.stringify(toCopy, null, 2);
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy:', err);
    }
  };
  const bumpTree = (): void => {
    setTreeKey(k => k + 1);
  };
  const showNoMatches = filterQuery.trim() !== '' && filteredValue === undefined;
  return <Modal>
            <ModalTrigger asChild>
                <DebugPill>
                    View Props
                </DebugPill>
            </ModalTrigger>
            <ModalContent {..._mergeComponentProps({
      overlayCss: propsBrowserOverlayCss,
      css: propsBrowserContentCss
    })}>
                <ModalDescription {..._mergeComponentProps({
        css: _staticStyles3
      })}>
                    Browse component properties as an expandable JSON tree; filter keys and values, or copy JSON matching the current view (filtered when a filter is set, otherwise full props).
                </ModalDescription>
                <div className="props-browser__div-1kvgdw">
                    <ModalTitle asChild>
                        <Typography fontSize="2xl" fontWeight="medium">
                            Properties
                        </Typography>
                    </ModalTitle>
                    <div className="props-browser__div-3cn4x4">
                        <Button {..._mergeComponentProps({
            onClick: handleCopy,
            css: _withVars(_staticStyles4, {
              "--static-styles-3vel4qkc_backgroundColor": _px(copied ? '#10b981' : '#3b82f6'),
              "--static-styles-21gxuxye_backgroundColor": _px(copied ? '#059669' : '#2563eb')
            })
          })}>
                            {copied ? 'Copied!' : 'Copy JSON'}
                        </Button>
                        <ModalCloseButton {..._mergeComponentProps({
            css: _staticStyles5
          })}>
                            ×
                        </ModalCloseButton>
                    </div>
                </div>

                <div className="props-browser__div-0a5mye">
                    <input type="search" placeholder="Filter keys or values..." value={filterQuery} onChange={e => {
          setFilterQuery(e.target.value);
        }} aria-label="Filter properties" className="props-browser__input-3nio9z" />
                    {filterQuery !== '' && <Button {..._mergeComponentProps({
          type: "button",
          onClick: () => {
            setFilterQuery('');
          },
          css: _staticStyles6
        })}>
                            Clear filter
                        </Button>}
                    <Button {..._mergeComponentProps({
          type: "button",
          onClick: () => {
            setCollapsedMode('expandAll');
            bumpTree();
          },
          css: _staticStyles7
        })}>
                        Expand all
                    </Button>
                    <Button {..._mergeComponentProps({
          type: "button",
          onClick: () => {
            setCollapsedMode('collapseAll');
            bumpTree();
          },
          css: _staticStyles7
        })}>
                        Collapse all
                    </Button>
                    <Button {..._mergeComponentProps({
          type: "button",
          onClick: () => {
            setCollapsedMode('depth');
            bumpTree();
          },
          css: _staticStyles7
        })}>
                        Default depth
                    </Button>
                </div>

                <div className="props-browser__div-23tv6x">
                    {showNoMatches ? <p className="props-browser__p-09042y">
                            No properties match your filter.
                        </p> : <JsonView key={treeKey} src={filteredValue ?? null} theme="vscode" collapsed={collapsedProp} editable={false} enableClipboard={false} displaySize="collapsed" style={{
          backgroundColor: 'transparent'
        }} className="json-view" />}
                </div>
            </ModalContent>
        </Modal>;
};
export default PropsBrowser;